import React, { useEffect, useMemo, useState } from 'react'
import { Box, Container, Link, Typography, withStyles } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import { componentBoxHideAction } from 'src/reducers/component-box/action'
import UserType from 'src/model/user-types'
import PLANS from '../pricing/data'
import { PricingCard, TableInvoice, TrustedBy } from '../pricing/components'
import style from './style'
import { roleTypes, AUDIENCES, AUDIENCE_BY_GROUP } from '../pricing/data/pricing-json'
import {
  handlePortalRedirection,
  handleStripeCheckout,
  trackPlanUpgrade,
  getAction,
  getUpgradeAction,
  getCheckoutMode,
} from '../pricing/logic'
import { meService } from 'src/api/authentication'
import Page from 'src/components/Page'
import Logo from 'src/components/logo/index'
import WorkspaceSwitcher from 'src/components/workspace-switcher/WorkspaceSwitcher'

const TrialExpired = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const [timeInterval, setTimeInterval] = useState(true)
  const [audience, setAudience] = useState(AUDIENCES.CREATOR)
  const [loading, setLoading] = useState(false)
  const [groupId, setGroupId] = useState(-1)
  const [emailType, setEmailType] = useState(null)
  const [trialExpired, setTrialExpired] = useState(false)
  const [type, setType] = useState('')

  const handleToggle = value => {
    setTimeInterval(value)
  }

  const getGroupIdById = id => {
    const role = roleTypes.find(role => role.id === id)
    return role ? role.groupId : undefined
  }

  useEffect(() => {
    dispatch(componentBoxHideAction())

    meService().then(response => {
      const userType = response.data.userRoleId
      const groupId = getGroupIdById(userType)
      setGroupId(groupId)
      setEmailType(response.data.emailType)
      setTrialExpired(response.data.trialExpired || false)
      setType(UserType[userType])
      // Open on the tab the user's current plan belongs to.
      setAudience(AUDIENCE_BY_GROUP[groupId] ?? AUDIENCES.CREATOR)
    })
  }, [])

  const visiblePlans = useMemo(
    () =>
      PLANS({ timeInterval, groupId, getAction, getUpgradeAction }).filter(
        plan => plan.isAllPlansInclude || plan.audience === audience,
      ),
    [timeInterval, groupId, audience],
  )

  const handleClick = async card => {
    const { priceId, priceTrialDays, successReturnUrl, subscription } = card

    // Plans without a Stripe price (Plus / Pro v2 / Launch) aren't purchasable
    // yet. Their buttons are already disabled; bail before we track or redirect.
    if (!priceId) return

    setLoading(true)
    trackPlanUpgrade(subscription)

    try {
      const mode = getCheckoutMode(groupId)
      if (mode === 'checkout') {
        await handleStripeCheckout(priceId, priceTrialDays, successReturnUrl)
      } else if (mode === 'portal') {
        await handlePortalRedirection()
      }
    } catch (error) {
      console.error('Plan upgrade error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleContactSupportClick = event => {
    event.preventDefault()

    if (window.Intercom) {
      window.Intercom('showMessages')
      return
    }

    window.location.href = 'mailto:support@minicoursegenerator.com'
  }

  return (
    <Page title="Trial Expired | Mini-Course Generator">
      <Box className={classes.navBar}>
        <Container maxWidth="xl" className={classes.navBarInner}>
          <Logo style={{ margin: 0 }} />
          <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <WorkspaceSwitcher />
            <Typography className={classes.contactSupport}>
            Questions? We're here to help.{' '}
            <Link
              href="mailto:support@minicoursegenerator.com"
              onClick={handleContactSupportClick}
              className={classes.contactLink}
            >
              Contact support
            </Link>
          </Typography>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="xl" className={classes.root}>
        <Box className={classes.trialBanner}>
          {type && !trialExpired && (
            <Box
              className={classes.currentPlanBadge}
              style={{
                backgroundColor:
                  UserType.isTrialEquivalent(type) ||
                  type === 'Basic' ||
                  (type && type.includes('AppSumo'))
                    ? '#E5E7EB'
                    : '#EFFCEF',
                color:
                  UserType.isTrialEquivalent(type) ||
                  type === 'Basic' ||
                  (type && type.includes('AppSumo'))
                    ? '#002E47'
                    : '#02791A',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <circle cx="5" cy="5" r="5" fill={
                  UserType.isTrialEquivalent(type) ||
                  type === 'Basic' ||
                  (type && type.includes('AppSumo'))
                    ? '#002E47'
                    : '#02791A'
                } />
              </svg>
              Current Plan: {UserType.getDisplayRoleName(type)}
            </Box>
          )}
          {emailType !== 1 && (
            <Typography align="center" className={classes.trialSubtitle}>
              Your 14-day trial period has ended.
            </Typography>
          )}
          <Typography align="center" className={classes.trialTitle}>
            Upgrade your plan to create impact{'\n'}and grow your business.
          </Typography>
        </Box>
        <PricingCard
          plans={visiblePlans}
          audience={audience}
          loading={loading}
          handleClick={handleClick}
          renderToggle={() => (
            <>
              <Box className={classes.audienceToggle}>
                <Box
                  className={`${classes.audienceOption} ${audience === AUDIENCES.CREATOR ? classes.audienceActive : ''}`}
                  onClick={() => setAudience(AUDIENCES.CREATOR)}
                >
                  Creator
                </Box>
                <Box
                  className={`${classes.audienceOption} ${audience === AUDIENCES.ENTERPRISE ? classes.audienceActive : ''}`}
                  onClick={() => setAudience(AUDIENCES.ENTERPRISE)}
                >
                  Enterprise
                </Box>
              </Box>
              <Box className={classes.billingToggle}>
                <span
                  className={`${classes.billingLabel} ${!timeInterval ? classes.billingLabelActive : ''}`}
                  onClick={() => handleToggle(false)}
                >
                  Monthly
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={timeInterval}
                  aria-label="Toggle annual billing"
                  className={`${classes.billingSwitch} ${timeInterval ? classes.billingSwitchOn : ''}`}
                  onClick={() => handleToggle(!timeInterval)}
                >
                  <span className={`${classes.billingKnob} ${timeInterval ? classes.billingKnobOn : ''}`}>
                    {timeInterval && (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#015FFB"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </span>
                </button>
                <span
                  className={`${classes.billingLabel} ${timeInterval ? classes.billingLabelActive : ''}`}
                  onClick={() => handleToggle(true)}
                >
                  Yearly
                </span>
                <span className={`${classes.billingBadge} ${timeInterval ? classes.billingBadgeOn : ''}`}>
                  Save up to 20%
                </span>
              </Box>
            </>
          )}
        />
        <TrustedBy />
        <div className={classes.rootHeader}>
          <Typography align="center" className={classes.typographyHeader}>
            Compare All Features
          </Typography>
        </div>
        <TableInvoice
          plans={visiblePlans}
          loading={loading}
          handleClick={handleClick}
        />
      </Container>
    </Page>
  )
}

export default withStyles(style)(TrialExpired)
